// Gestione centralizzata dei meta tag SEO per ogni pagina e lingua.
// Viene eseguita a runtime e finisce anche negli snapshot pre-renderizzati
// (react-snap), quindi crawler e social vedono title/description/canonical/
// hreflang corretti per ogni URL.

const BASE = "https://www.studioprojectworks.com";

const META = {
  "/": {
    it: {
      title: "Project Works | Studio Tecnico Associato — Progettazione, Rilievi, Direzione Lavori",
      description:
        "Project Works è uno Studio Tecnico Associato a Nizza Monferrato (AT): progettazione edilizia, rilievi topografici, direzione lavori e consulenze tecniche. Richiedi un preventivo.",
    },
    en: {
      title: "Project Works | Associated Technical Studio — Design, Surveys, Construction Management",
      description:
        "Project Works is an associated technical studio in Nizza Monferrato (AT), Italy: building design, topographic surveys, construction management and technical consulting. Request a quote.",
    },
  },
  "/privacy-policy/": {
    it: {
      title: "Privacy Policy | Project Works — Studio Tecnico Associato",
      description: "Privacy Policy di Project Works — Studio Tecnico Associato, Nizza Monferrato (AT).",
    },
    en: {
      title: "Privacy Policy | Project Works — Associated Technical Studio",
      description: "Privacy Policy of Project Works — Associated Technical Studio, Nizza Monferrato (AT), Italy.",
    },
  },
  "/progetti/relax-in-cascina/": {
    it: {
      title: "Relax in Cascina | Ristrutturazione nel Monferrato | Project Works",
      description:
        "Recupero e ristrutturazione di un cascinale nel Monferrato: rilievo dello stato iniziale, progetto, cantiere e risultato. Un dialogo tra architettura esistente e comfort contemporaneo.",
    },
    en: {
      title: "Relax in Cascina | Farmhouse Renovation in Monferrato | Project Works",
      description:
        "Recovery and renovation of a farmhouse in Monferrato, Italy: survey of the original state, design, construction site and result. A dialogue between existing architecture and contemporary comfort.",
    },
  },
  "/progetti/casa-sulla-collina/": {
    it: {
      title: "La Casa sulla Collina | Demolizione e Ricostruzione nell'Alto Monferrato | Project Works",
      description:
        "Demolizione e ricostruzione di un rustico nell'Alto Monferrato: recupero della volumetria, ampliamenti e nuova abitazione di 330 m² con pertinenze e piscina. Dal progetto al cantiere al risultato.",
    },
    en: {
      title: "La Casa sulla Collina | Demolition and Reconstruction in Alto Monferrato | Project Works",
      description:
        "Demolition and reconstruction of a country house in Alto Monferrato, Italy: recovery of the original volume, extensions and a new 330 m² home with outbuildings and swimming pool. From design to the result.",
    },
  },
  "/progetti/vivere-in-un-sito-unesco/": {
    it: {
      title: "Vivere in un sito UNESCO | Ristrutturazione Cascinale nell'Alto Monferrato | Project Works",
      description:
        "Ristrutturazione di un cascinale ottocentesco di 600 m² nell'Alto Monferrato, nel paesaggio vitivinicolo Patrimonio UNESCO: recupero dei materiali originari, nuovi spazi e dialogo con il paesaggio.",
    },
    en: {
      title: "Vivere in un sito UNESCO | Historic Farmhouse Renovation in Alto Monferrato | Project Works",
      description:
        "Renovation of a 19th-century 600 m² farmhouse in Alto Monferrato, Italy, in the UNESCO World Heritage vineyard landscape: recovery of original materials, new spaces and dialogue with the landscape.",
    },
  },
  "/progetti/vivere-in-centro-storico/": {
    it: {
      title: "Vivere in Centro Storico | Piano di Recupero a Nizza Monferrato | Project Works",
      description:
        "Recupero e ampliamento di un complesso immobiliare di 700 m² nel centro storico di Nizza Monferrato: appartamenti, locali commerciali e autorimesse organizzati attorno al cortile interno.",
    },
    en: {
      title: "Vivere in Centro Storico | Building Recovery in Nizza Monferrato | Project Works",
      description:
        "Recovery and extension of a 700 m² building complex in the historic centre of Nizza Monferrato, Italy: apartments, commercial premises and garages arranged around the inner courtyard.",
    },
  },
  "/cookie-policy/": {
    it: {
      title: "Cookie Policy | Project Works — Studio Tecnico Associato",
      description: "Cookie Policy di Project Works — Studio Tecnico Associato, Nizza Monferrato (AT).",
    },
    en: {
      title: "Cookie Policy | Project Works — Associated Technical Studio",
      description: "Cookie Policy of Project Works — Associated Technical Studio, Nizza Monferrato (AT), Italy.",
    },
  },
};

// Percorso canonico: le pagine legali sono directory reali, il canonical ha lo slash finale
const canonicalPath = (pathname) => {
  if (pathname === "/") return "/";
  if (pathname === "/en" || pathname === "/en/") return "/en/";
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
};

const upsertMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertHreflang = (lang, href) => {
  let el = document.querySelector(`link[hreflang="${lang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", lang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export function applyHeadMeta(pathname, lang) {
  const cp = canonicalPath(pathname);
  const key = cp === "/" || cp === "/en/" ? "/" : cp.replace(/^\/en/, "");
  const m = META[key]?.[lang];

  document.documentElement.lang = lang;
  if (m) {
    document.title = m.title;
    upsertMeta("name", "description", m.description);
    upsertMeta("property", "og:title", m.title);
    upsertMeta("property", "og:description", m.description);
  }
  upsertLink("canonical", BASE + cp);
  upsertMeta("property", "og:url", BASE + cp);
  upsertMeta("property", "og:locale", lang === "en" ? "en_US" : "it_IT");
  upsertMeta("property", "og:site_name", lang === "en" ? "Project Works — Associated Technical Studio" : "Project Works — Studio Tecnico Associato");

  const itPath = cp.replace(/^\/en/, "") || "/";
  const enPath = itPath === "/" ? "/en/" : `/en${itPath}`;
  upsertHreflang("it", BASE + itPath);
  upsertHreflang("en", BASE + enPath);
  upsertHreflang("x-default", BASE + itPath);
}
